import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const MEMBER_HOME_PATH = '/m';

/**
 * Returns a handler for a "back" button that falls back to `fallbackPath`
 * (defaults to the member home screen) when there is no in-app history to
 * go back to - e.g. the page was opened directly via a deep link (email,
 * bookmark, shared URL) rather than navigated to from within the app.
 *
 * `location.key` is not a reliable signal here: React Router assigns a
 * fresh key on a `replace` navigation too, even though `replace` does not
 * add a new history entry. Post-auth redirects (e.g. DiscoveryCallback)
 * land on their target via `navigate(..., { replace: true })`, so a
 * key-based check would treat that first real entry as having history
 * behind it. Instead, this reads the history index React Router stores in
 * `window.history.state.idx`, which `replace` does not increment - index
 * `0` means this is genuinely the first entry in the tab's history.
 */
export const useBackNavigation = (fallbackPath: string = MEMBER_HOME_PATH) => {
  const navigate = useNavigate();

  return useCallback(() => {
    const historyIndex = window.history.state?.idx;

    if (
      historyIndex === 0 ||
      historyIndex === undefined ||
      historyIndex === null
    ) {
      navigate(fallbackPath, { replace: true });
    } else {
      navigate(-1);
    }
  }, [navigate, fallbackPath]);
};
